/**
 * Aurexa — `<ax-frequently-bought>` (`assets/ax-frequently-bought.js`)
 * =============================================================================
 * The "frequently bought together" bundle: it fetches the rest of the set, keeps
 * a running total of what is ticked, and sends the whole set to the cart as ONE
 * `Aurexa.cart.add(items)` call.
 *
 * Rendered by `sections/frequently-bought.liquid` and
 * `snippets/frequently-bought-row.liquid`, styled by
 * `assets/section-frequently-bought.css`. Zero dependencies. It is the sibling
 * of `assets/ax-bundle-builder.js` and deliberately shares its shape — one
 * batched cart call, money only ever through `Aurexa.money()`, every sentence
 * arriving from Liquid as a finished template — and it borrows the fetch from
 * `assets/ax-recommendations.js`, which is the file that owns this pattern.
 *
 * CONTRACT
 *   <ax-frequently-bought data-url="/recommendations/products?…&intent=complementary"
 *                         data-url-alt="/recommendations/products?…&intent=related"
 *                         data-cart-type="drawer" data-cart-drawer-id="ax-cart-drawer"
 *                         data-total-label="Total" data-total-empty="…"
 *                         data-add-none="…" data-add-one="Add 1 item"
 *                         data-add-other="Add __AX_COUNT__ items"
 *                         data-error-empty="…" data-count-token="__AX_COUNT__"
 *                         data-ax-fbt-autohide>
 *     <ul data-ax-fbt-media aria-hidden="true">
 *       <li data-ax-fbt-thumb data-ax-fbt-key="123" data-ax-fbt-picked="true">
 *     <ul data-ax-fbt-list role="list">
 *       <li data-ax-fbt-row data-ax-fbt-key data-variant-id data-price data-available
 *           [data-ax-fbt-locked]>
 *         <input type="checkbox" data-ax-fbt-tick>
 *         <select data-ax-fbt-variants><option value data-price>
 *     <p data-ax-fbt-total aria-live="polite" aria-atomic="true">
 *     <button data-ax-fbt-submit><span data-ax-fbt-add-label>
 *     <div data-ax-fbt-error role="alert" hidden><span data-ax-fbt-error-text>
 *
 *   Nothing here is found by position — every lookup is by attribute — but the
 *   order is the markup's, and the markup puts the button after the ticks it
 *   adds up on purpose.
 *
 *   The fetched response carries only
 *   `<div data-ax-fbt-body data-ax-count="N"><template data-ax-fbt-media>…
 *   <template data-ax-fbt-rows>…`, which this file appends to the two lists
 *   already on the page. The panel, its total and its button are never replaced.
 *
 * TWO INTENTS
 *   `data-url` is asked first. A body reporting `data-ax-count="0"` means the
 *   merchant has not curated that intent, and `data-url-alt` — when the section
 *   supplied one — is asked once instead. Measured on the store this ships to,
 *   the `complementary` endpoint is empty for every product sampled and
 *   `related` is full, so without the second ask the section would be a
 *   permanent blank.
 *
 * WHICH VARIANT
 *   A row with a `<select>` is always read from that select's selected option,
 *   never from the row's own `data-variant-id` — those attributes describe the
 *   variant Liquid rendered, and treating them as a cache is how a shopper ends
 *   up with the size they did not choose. For the same reason the current
 *   product's row follows the buy box: `variant:changed` on the Aurexa bus moves
 *   its select, the way `assets/ax-sticky-atc.js` moves its own.
 *
 * MONEY
 *   Only the running total is formatted here, by `Aurexa.money()` from the
 *   shop's own Liquid money format (ARCHITECTURE §6.4). Every per-product and
 *   per-variant figure is rendered by Liquid.
 *
 *   A money format is not necessarily text. It is a merchant-side Liquid
 *   template and may carry elements — on the AED store this was measured on the
 *   dirham symbol is a 912-character inline `<svg>` — so the string
 *   `Aurexa.money()` returns can be an HTML fragment. It is parsed, never
 *   written with `textContent`, which would print its source. Only it is
 *   parsed; every sentence around it is a text node. See `moneyNode()`.
 *
 * ANNOUNCEMENTS
 *   The total is the only live region, and it is written only when its text
 *   actually changed, so moving through a variant dropdown does not read a
 *   total per keystroke. The bundle's first fill happens BEFORE the section is
 *   revealed, so the region is already correct when it enters the accessibility
 *   tree and the arrival of the bundle is not announced as a change.
 * =============================================================================
 */

(function () {
  'use strict';

  if (customElements.get('ax-frequently-bought')) return;

  var TAG = 'ax-frequently-bought';

  /** How far ahead of the viewport the bundle starts loading — as ax-recommendations. */
  var ROOT_MARGIN = '600px 0px';

  /** @returns {Object|null} The Aurexa runtime, when it has booted. */
  function runtime() {
    return window.Aurexa && window.Aurexa.__core === true ? window.Aurexa : null;
  }

  /**
   * @param {string} key
   * @param {Object} [vars]
   * @returns {string}
   */
  function t(key, vars) {
    var api = runtime();
    return api && typeof api.t === 'function' ? api.t(key, vars) : '';
  }

  /**
   * Subscribe to an Aurexa bus event, with the same document-level fallback
   * `assets/ax-sticky-atc.js` uses when the runtime has not booted.
   * @param {string} name
   * @param {Function} fn
   * @returns {Function} Unsubscribe.
   */
  function on(name, fn) {
    var api = runtime();
    if (api && typeof api.on === 'function') return api.on(name, fn);
    var handler = function (event) {
      fn(event.detail, name);
    };
    document.addEventListener('aurexa:' + name, handler);
    return function () {
      document.removeEventListener('aurexa:' + name, handler);
    };
  }

  /**
   * @param {*} value
   * @param {number} fallback
   * @returns {number}
   */
  function num(value, fallback) {
    var parsed = typeof value === 'number' ? value : parseFloat(value);
    return isFinite(parsed) ? parsed : fallback;
  }

  /**
   * Format an amount in cents with the shop's money format.
   * @param {number} cents
   * @returns {string} Possibly an HTML fragment — the money format may be markup.
   */
  function money(cents) {
    var api = runtime();
    if (api && typeof api.money === 'function') return api.money(cents);
    return String(Math.round(cents) / 100);
  }

  /**
   * Parse a formatted amount into nodes.
   *
   * A `<template>` parses it, so its nodes go in with no wrapper around them —
   * the same nodes, in the same order, that Liquid's own first paint of this
   * amount produced. A `<script>` parsed out of a string is marked
   * already-started by the fragment parser and never runs, here or once moved.
   * @param {number} cents
   * @returns {DocumentFragment}
   */
  function moneyNode(cents) {
    var slot = document.createElement('template');
    slot.innerHTML = money(cents);
    return slot.content;
  }

  /**
   * Strip every space, so two renderings of the same total compare equal.
   *
   * COLLAPSING whitespace is not enough here, and that is the whole point of
   * this function. Liquid writes the label and the amount as two elements on
   * two indented lines, so its text is "Total\n  AED 648.99"; the fragment this
   * file builds appends the two spans with nothing between them, so its text is
   * "TotalAED 648.99". Collapsed, those differ by one space — and the total is
   * an aria-live region whose whole contract is that it is written only when it
   * really changed. The first paint would rewrite it every time, throwing away
   * Liquid's own money nodes (on this client's AED store, a 912-character
   * inline <svg>) and swapping them for `Aurexa.money()`'s. Removing the spaces
   * compares the two the only way that survives the Liquid/JavaScript boundary;
   * no money format distinguishes two amounts by whitespace alone.
   * @param {string} value
   * @returns {string}
   */
  function flat(value) {
    return String(value || '').replace(/\s+/g, '');
  }

  /**
   * `<ax-frequently-bought>` — the bundle, its total and its one batched add.
   */
  class AxFrequentlyBought extends HTMLElement {
    constructor() {
      super();
      this.busy = false;
      this.filled = false;
      /** @type {IntersectionObserver|null} */
      this.observer = null;
      /** @type {AbortController|null} */
      this.controller = null;
      /** @type {Function[]} */
      this.unbind = [];
      this.onChange = this.handleChange.bind(this);
      this.onClick = this.handleClick.bind(this);
      this.onSectionLoad = this.handleSectionLoad.bind(this);
      this.onVariantChanged = this.handleVariantChanged.bind(this);
    }

    /** @returns {void} */
    connectedCallback() {
      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);
      document.addEventListener('shopify:section:load', this.onSectionLoad);
      this.unbind.push(on('variant:changed', this.onVariantChanged));
      this.paint();
      this.schedule();
    }

    /** @returns {void} */
    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('click', this.onClick);
      document.removeEventListener('shopify:section:load', this.onSectionLoad);
      this.unbind.forEach(function (off) {
        if (typeof off === 'function') off();
      });
      this.unbind = [];
      this.disconnectObserver();
      if (this.controller && typeof this.controller.abort === 'function') this.controller.abort();
      this.controller = null;
    }

    /* ---------------------------------------------------------------------
     * Fetching the rest of the bundle
     * ------------------------------------------------------------------ */

    /**
     * The section element this lives in — the box whose padding collapses while
     * the bundle is empty. `closest` is called on the PARENT, never on `this`:
     * an element that matched itself would become its own "section root" and
     * collapsing it would leave the real section's heading and padding behind.
     * That was the orphan-heading bug ax-recommendations documents.
     * @returns {Element|null}
     */
    get sectionRoot() {
      var parent = this.parentElement;
      return parent ? parent.closest('[data-section-id]') : null;
    }

    /** @returns {void} */
    disconnectObserver() {
      if (this.observer) this.observer.disconnect();
      this.observer = null;
    }

    /** @returns {void} */
    schedule() {
      if (this.filled) return;

      var endpoint = this.getAttribute('data-url');
      if (!endpoint) {
        this.settle(0);
        return;
      }

      if (typeof IntersectionObserver !== 'function') {
        this.load(endpoint, false);
        return;
      }

      this.observer = new IntersectionObserver(
        function (entries) {
          var visible = entries.some(function (entry) {
            return entry.isIntersecting;
          });
          if (!visible) return;
          this.disconnectObserver();
          this.load(endpoint, false);
        }.bind(this),
        { rootMargin: ROOT_MARGIN }
      );
      this.observer.observe(this);
    }

    /**
     * @param {string} endpoint
     * @param {boolean} isFallback Whether this is already the second intent.
     * @returns {Promise<void>}
     */
    load(endpoint, isFallback) {
      var controller = typeof AbortController === 'function' ? new AbortController() : null;
      this.controller = controller;
      this.setAttribute('aria-busy', 'true');

      return fetch(endpoint, {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal: controller ? controller.signal : undefined
      })
        .then(function (response) {
          if (!response.ok) throw new Error('Recommendations request failed: ' + response.status);
          return response.text();
        })
        .then(
          function (html) {
            if (!this.isConnected) return;
            this.fill(html, isFallback);
          }.bind(this)
        )
        .catch(
          function (error) {
            if (error && error.name === 'AbortError') return;
            this.settle(0);
          }.bind(this)
        )
        .finally(
          function () {
            /* Only the request still in flight owns `aria-busy`. When this one
               came back empty and handed over to the second intent, `load()`
               has already replaced the controller, and clearing the flag here
               would say "finished" while the bundle is still being fetched. */
            if (this.controller === controller) this.removeAttribute('aria-busy');
          }.bind(this)
        );
    }

    /**
     * Append what came back.
     *
     * The response's `data-ax-count` is the number of products the section
     * actually DREW, not the number the API returned, so a body of nothing but
     * sold-out suggestions counts as zero and the second intent is asked for.
     * @param {string} html
     * @param {boolean} isFallback
     * @returns {void}
     */
    fill(html, isFallback) {
      /* Filling is an APPEND, so it has to be able to happen only once. The
         normal paths already guarantee that — the observer fires once and the
         second intent is asked for at most once — but a caller that reached
         `load()` another way would otherwise double the bundle. */
      if (this.filled) return;

      var parsed = document.createElement('template');
      parsed.innerHTML = String(html).trim();

      var body = parsed.content.querySelector('[data-ax-fbt-body]');
      var count = body ? parseInt(body.getAttribute('data-ax-count'), 10) : 0;

      if (!body || !(count > 0)) {
        var alt = this.getAttribute('data-url-alt');
        if (!isFallback && alt) {
          this.load(alt, true);
          return;
        }
        this.settle(0);
        return;
      }

      var media = this.querySelector('[data-ax-fbt-media]');
      var list = this.querySelector('[data-ax-fbt-list]');
      var srcMedia = body.querySelector('template[data-ax-fbt-media]');
      var srcRows = body.querySelector('template[data-ax-fbt-rows]');

      if (media && srcMedia) media.appendChild(srcMedia.content);
      if (list && srcRows) list.appendChild(srcRows.content);

      this.filled = true;

      /* Paint BEFORE revealing. The total is a live region: writing it while the
         panel is still `display: none` means its final text is what enters the
         accessibility tree, so the bundle arriving is new content rather than a
         change to be announced. */
      this.paint();
      this.settle(count);

      /* A freshly appended row can carry markup other runtimes own. */
      var api = runtime();
      if (api && typeof api.sync === 'function') api.sync();
    }

    /**
     * Record whether the bundle has anything in it.
     *
     * Only a section that asked to be hidden is hidden: in the theme editor the
     * markup omits `data-ax-fbt-autohide`, so an empty bundle stays selectable
     * and its editor note stays readable.
     * @param {number} count
     * @returns {void}
     */
    settle(count) {
      var root = this.sectionRoot;
      if (!root) return;
      if (count > 0) {
        root.setAttribute('data-ax-fbt-empty', 'false');
        return;
      }
      if (this.hasAttribute('data-ax-fbt-autohide')) root.setAttribute('data-ax-fbt-empty', 'true');
    }

    /* ---------------------------------------------------------------------
     * Lookups
     * ------------------------------------------------------------------ */

    /** @returns {Element[]} */
    get rows() {
      return Array.prototype.slice.call(this.querySelectorAll('[data-ax-fbt-row]'));
    }

    /** @returns {HTMLButtonElement|null} */
    get submitButton() {
      var el = this.querySelector('[data-ax-fbt-submit]');
      return el instanceof HTMLButtonElement ? el : null;
    }

    /**
     * @param {Element} row
     * @returns {HTMLInputElement|null}
     */
    tickOf(row) {
      var el = row.querySelector('[data-ax-fbt-tick]');
      return el instanceof HTMLInputElement ? el : null;
    }

    /**
     * Whether this row is part of the bundle right now.
     *
     * A locked row — the product whose page this is — is in whether or not its
     * checkbox reports checked, because that checkbox is disabled: it is ticked
     * and cannot be unticked, and a disabled control is not a source of truth.
     * @param {Element} row
     * @returns {boolean}
     */
    isPicked(row) {
      if (row.getAttribute('data-available') === 'false') return false;
      if (row.hasAttribute('data-ax-fbt-locked')) return true;
      var tick = this.tickOf(row);
      return !!(tick && !tick.disabled && tick.checked);
    }

    /**
     * What this row would add right now.
     * @param {Element} row
     * @returns {{id: string, price: number}|null}
     */
    variantOf(row) {
      var select = row.querySelector('[data-ax-fbt-variants]');
      if (select instanceof HTMLSelectElement) {
        var option = select.options[select.selectedIndex];
        if (!option || option.disabled || !option.value) return null;
        return {
          id: option.value,
          price: Math.max(0, Math.round(num(option.getAttribute('data-price'), 0)))
        };
      }

      var id = row.getAttribute('data-variant-id') || '';
      if (!id) return null;
      return { id: id, price: Math.max(0, Math.round(num(row.getAttribute('data-price'), 0))) };
    }

    /**
     * Every picked, purchasable row.
     *
     * `rows` is the same list as `items`, in the same order: the row each line
     * item came from. Untick what was sent by walking it, never by walking every
     * row — a row this loop skipped never reached the cart.
     * @returns {{items: Array<{id: string, quantity: number}>, rows: Element[], lines: number, total: number}}
     */
    collect() {
      var items = [];
      var rows = [];
      var total = 0;

      this.rows.forEach(function (row) {
        if (!this.isPicked(row)) return;
        var variant = this.variantOf(row);
        if (!variant) return;
        items.push({ id: variant.id, quantity: 1 });
        rows.push(row);
        total += variant.price;
      }, this);

      return { items: items, rows: rows, lines: items.length, total: total };
    }

    /* ---------------------------------------------------------------------
     * Rendering
     * ------------------------------------------------------------------ */

    /** @returns {void} */
    paint() {
      var collected = this.collect();
      this.paintPicks(collected.rows);
      this.paintTotal(collected);
      this.paintButton(collected.lines);
    }

    /**
     * Mirror onto every row, and onto its thumbnail, whether it is in the
     * bundle right now.
     *
     * It is handed the rows `collect()` actually returned rather than asking
     * `isPicked()` again, so the picture, the total and the button's count can
     * never disagree: they are three readings of one pass. They could disagree
     * if this asked twice. A ticked row whose chosen variant cannot be bought
     * — reachable on the current product, whose `?variant=` may name a sold-out
     * variant of an otherwise available product — is dropped by `collect()`
     * because `variantOf()` refuses a disabled option, and the strip has to
     * drop it too or it would show a product the total does not include.
     *
     * The strip is a picture of the same list and nothing more — it is
     * `aria-hidden`, and the row beside it is where the state is stated. This
     * only keeps the picture honest.
     * @param {Element[]} inBundle The rows this paint is adding up.
     * @returns {void}
     */
    paintPicks(inBundle) {
      var picked = inBundle || [];
      var thumbs = {};
      Array.prototype.forEach.call(this.querySelectorAll('[data-ax-fbt-thumb]'), function (thumb) {
        thumbs[thumb.getAttribute('data-ax-fbt-key') || ''] = thumb;
      });

      this.rows.forEach(function (row) {
        var state = picked.indexOf(row) === -1 ? 'false' : 'true';
        row.setAttribute('data-ax-fbt-picked', state);
        var thumb = thumbs[row.getAttribute('data-ax-fbt-key') || ''];
        if (thumb) thumb.setAttribute('data-ax-fbt-picked', state);
      }, this);
    }

    /**
     * The running total, or the hint that stands in for it while nothing is
     * ticked.
     *
     * Built as nodes, not as a string: the amount may be markup and the words
     * around it never are. Written only when the text changed — this is the
     * section's one live region.
     * @param {{lines: number, total: number}} collected
     * @returns {void}
     */
    paintTotal(collected) {
      var host = this.querySelector('[data-ax-fbt-total]');
      if (!host) return;

      var next = document.createDocumentFragment();

      if (collected.lines === 0) {
        var hint = document.createElement('span');
        hint.className = 'ax-fbt__total-hint';
        hint.textContent = this.getAttribute('data-total-empty') || '';
        next.appendChild(hint);
      } else {
        var label = document.createElement('span');
        label.className = 'ax-fbt__total-label';
        label.textContent = this.getAttribute('data-total-label') || '';
        next.appendChild(label);

        var amount = document.createElement('span');
        amount.className = 'ax-fbt__total-money';
        amount.appendChild(moneyNode(collected.total));
        next.appendChild(amount);
      }

      if (flat(host.textContent) === flat(next.textContent)) return;
      host.replaceChildren(next);
    }

    /**
     * The button's label and its disabled state.
     * @param {number} count
     * @returns {void}
     */
    paintButton(count) {
      var button = this.submitButton;
      if (!button) return;

      var label = button.querySelector('[data-ax-fbt-add-label]');
      if (label) {
        var token = this.getAttribute('data-count-token') || '__AX_COUNT__';
        var template;
        if (count === 0) template = this.getAttribute('data-add-none') || '';
        else if (count === 1) template = this.getAttribute('data-add-one') || '';
        else template = this.getAttribute('data-add-other') || '';
        /* The count is a number, so the finished sentence is text and goes in
           as text. No money is ever interpolated into it. */
        var text = String(template).split(token).join(String(count));
        if (label.textContent !== text) label.textContent = text;
      }

      button.setAttribute('data-ax-empty', count === 0 ? 'true' : 'false');
      this.syncDisabled();
    }

    /**
     * The one place the button's `disabled` is written.
     *
     * It refuses to act for exactly two reasons — nothing is ticked, or a
     * request is in flight — and the reason is always readable: the total line
     * (which the button is described by, and which is the live region) says what
     * is missing, and `aria-busy` says the other. Disabling while the button
     * holds focus would drop the shopper out of the tab order, so focus is
     * handed back to the list first.
     * @returns {void}
     */
    syncDisabled() {
      var button = this.submitButton;
      if (!button) return;

      var next = this.busy || button.getAttribute('data-ax-empty') === 'true';
      if (next === button.disabled) return;

      if (next && document.activeElement === button) this.focusFirstTick();
      button.disabled = next;
    }

    /** @returns {boolean} Whether a tick could be focused. */
    focusFirstTick() {
      var tick = this.querySelector('[data-ax-fbt-tick]:not(:disabled)');
      if (!(tick instanceof HTMLElement)) return false;
      tick.focus();
      return true;
    }

    /**
     * @param {string} message
     * @returns {void}
     */
    showError(message) {
      var box = this.querySelector('[data-ax-fbt-error]');
      if (!box) return;
      var text = box.querySelector('[data-ax-fbt-error-text]') || box;
      text.textContent = message || '';
      box.hidden = !message;
    }

    /**
     * @param {boolean} busy
     * @returns {void}
     */
    setBusy(busy) {
      this.busy = !!busy;
      var button = this.submitButton;
      if (!button) return;
      if (busy) button.setAttribute('aria-busy', 'true');
      else button.removeAttribute('aria-busy');
      this.syncDisabled();
    }

    /* ---------------------------------------------------------------------
     * Adding
     * ------------------------------------------------------------------ */

    /**
     * Send the whole bundle as one request.
     * @param {Array<{id: string, quantity: number}>} items
     * @param {Element} [trigger] Control that started this, for focus return.
     * @param {Element[]} rows The rows those line items came from.
     * @returns {void}
     */
    add(items, trigger, rows) {
      var api = runtime();
      if (!api || !api.cart || typeof api.cart.add !== 'function') return;
      if (this.busy) return;

      this.showError('');
      this.setBusy(true);

      api.cart
        .add(items)
        .then(
          function (result) {
            this.reset(rows);
            this.afterAdd(result, trigger);
          }.bind(this)
        )
        .catch(
          function (error) {
            var message = (error && error.message) || t('cartError') || t('error');
            this.showError(message);
            if (typeof api.toast === 'function') api.toast(message, 'danger');
          }.bind(this)
        )
        .finally(
          function () {
            this.setBusy(false);
          }.bind(this)
        );
    }

    /**
     * Untick the rows whose line items reached the cart, so the button cannot
     * silently order them a second time.
     *
     * The locked row is the exception, and it has to be: its checkbox is
     * disabled, so there is nothing to untick. Pressing the button again adds
     * the product the shopper is looking at a second time — exactly what a
     * second press of the buy box's own Add to cart does, and the cart drawer
     * that opened over it says so.
     * @param {Element[]} rows The rows the cart accepted.
     * @returns {void}
     */
    reset(rows) {
      (rows || []).forEach(function (row) {
        var tick = this.tickOf(row);
        if (tick && !tick.disabled) tick.checked = false;
      }, this);
      this.paint();
    }

    /**
     * Route the shopper to whichever cart surface the merchant configured — the
     * same three outcomes `<ax-product-form>` and `<ax-bundle-builder>` offer.
     * @param {Object} result
     * @param {Element} [trigger]
     * @returns {void}
     */
    afterAdd(result, trigger) {
      var api = runtime();
      var type = this.getAttribute('data-cart-type') || (api && api.settings && api.settings.cartType) || 'drawer';

      if (type === 'page') {
        window.location.href = (api && api.routes && api.routes.cart_url) || '/cart';
        return;
      }

      if (type === 'drawer' && api && typeof api.open === 'function') {
        var drawerId = this.getAttribute('data-cart-drawer-id') || 'ax-cart-drawer';
        if (api.open(drawerId, trigger || this.submitButton)) return;
      }

      /* No drawer and no navigation: the toast is the confirmation. Focus is not
         moved here — `syncDisabled` already handed it back to the list if the
         button it was on had to be disabled. */
      var message = t('cartItemAdded') || t('added');
      if (message && api && typeof api.toast === 'function') api.toast(message, 'success');

      this.dispatchEvent(new CustomEvent('ax:added', { detail: result, bubbles: true }));
    }

    /* ---------------------------------------------------------------------
     * Handlers
     * ------------------------------------------------------------------ */

    /**
     * A tick or a variant both change what the bundle costs. `change`, never
     * `input`: the total is a live region and a keyboard user moving through a
     * dropdown must not be read one total per key.
     * @param {Event} event
     * @returns {void}
     */
    handleChange(event) {
      var target = event.target;
      if (!(target instanceof Element)) return;
      if (!target.matches('[data-ax-fbt-tick], [data-ax-fbt-variants]')) return;
      if (!target.closest('[data-ax-fbt-row]')) return;

      this.showError('');
      this.paint();
    }

    /**
     * @param {MouseEvent} event
     * @returns {void}
     */
    handleClick(event) {
      var target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      var button = target.closest('[data-ax-fbt-submit]');
      if (!button || !this.contains(button)) return;

      event.preventDefault();

      var collected = this.collect();
      if (!collected.items.length) {
        /* Reachable when the button is clicked before this element has caught up
           — a mouse-up on a control that is about to be disabled. Say what is
           missing rather than doing nothing. */
        this.showError(this.getAttribute('data-error-empty') || '');
        this.focusFirstTick();
        return;
      }

      this.add(collected.items, button, collected.rows);
    }

    /**
     * The buy box picked a different variant of the product this page is about.
     * Follow it, so the bundle adds the size the shopper actually chose — the
     * move `assets/ax-sticky-atc.js` makes for the same reason.
     *
     * The row is found by product id, so an event from a quick view of some
     * OTHER product, or from a featured-product section, matches nothing here.
     * Only a row that has a select can follow: a row without one has a single
     * variant, which is the only variant there is.
     * @param {{product: Object, variant: Object}} detail
     * @returns {void}
     */
    handleVariantChanged(detail) {
      if (!detail || !detail.variant || !detail.variant.id) return;

      var productId = '';
      if (detail.product && detail.product.id) productId = String(detail.product.id);
      else if (detail.variant.product_id) productId = String(detail.variant.product_id);
      if (!productId) return;

      var moved = false;
      this.rows.forEach(function (row) {
        if (row.getAttribute('data-ax-fbt-key') !== productId) return;
        var select = row.querySelector('[data-ax-fbt-variants]');
        if (!(select instanceof HTMLSelectElement)) return;
        var next = String(detail.variant.id);
        if (select.value === next) return;
        select.value = next;
        moved = true;
      });

      if (moved) this.paint();
    }

    /**
     * The theme editor re-renders a section wholesale; the running total has to
     * be recomputed against whatever markup came back.
     * @param {CustomEvent} event
     * @returns {void}
     */
    handleSectionLoad(event) {
      var target = event && event.target;
      if (target instanceof Element && !target.contains(this)) return;
      this.paint();
    }
  }

  customElements.define(TAG, AxFrequentlyBought);
})();
